/**
 * BoAdminInboxPage — Boîte mail admin (Resend inbound) — squelette MVP.
 * Route : /bo/admin/inbox
 *
 * NOTE: L'envoi Resend réel est encore MOCKED côté backend (attend config MX
 * du domaine + secret webhook — voir Phase 29.C.4).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Inbox, Mail, MailOpen, Archive, Trash2, Reply, AlertCircle, X, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const BO_BLUE = '#1d4ed8';

export default function BoAdminInboxPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [inboundStatus, setInboundStatus] = useState(null);

  useEffect(() => {
    document.title = 'Inbox admin · Back-office';
    reload();
    // Charge le status Resend pour badge dynamique
    api.boInboundStatus().then(setInboundStatus).catch(() => setInboundStatus(null));
  }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.boListInbox(false);
      setItems(r.items || []);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate('/login', { state: { from: '/bo/admin/inbox' } });
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRead = async (mail, read) => {
    try {
      await api.boPatchInbox(mail.id, { read });
      reload();
    } catch (e) { setError(e.message); }
  };

  const handleArchive = async (mail) => {
    try {
      await api.boPatchInbox(mail.id, { archived: true });
      setSelected(null);
      reload();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (mail) => {
    if (!window.confirm(`Supprimer définitivement l'email de "${mail.from_email}" ?`)) return;
    try {
      await api.boDeleteInbox(mail.id);
      setSelected(null);
      reload();
    } catch (e) { setError(e.message); }
  };

  const handleSendReply = async () => {
    if (!replyBody.trim() || !selected) return;
    setSending(true);
    try {
      const r = await api.boReplyInbox(selected.id, replyBody.trim());
      setReplyBody('');
      if (r.ok && !r.mocked_send) {
        toast.success(`Réponse envoyée à ${selected.from_email} via Resend`);
      } else if (r.mocked_send) {
        toast.warning('Réponse enregistrée (envoi mocké — config Resend incomplète)');
      } else {
        toast.error(`Échec envoi : ${r.delivery?.error || 'erreur inconnue'}`);
      }
      reload();
    } catch (e) {
      toast.error(e.message || 'Envoi impossible');
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="bo-inbox">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      <div className="flex items-baseline justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BO_BLUE }}>
            Admin · Boîte mail
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Inbox ({items.length})
          </h1>
        </div>
        {inboundStatus && inboundStatus.mode === 'production' ? (
          <span
            className="text-[10px] px-2 py-1 rounded text-white font-black inline-flex items-center gap-1"
            style={{ background: '#059669' }}
            data-testid="inbox-mode-badge"
          >
            ✓ Resend production
          </span>
        ) : inboundStatus ? (
          <span
            className="text-[10px] px-2 py-1 rounded text-white font-black"
            style={{ background: '#f59e0b' }}
            data-testid="inbox-mode-badge"
            title="Il manque RESEND_WEBHOOK_SECRET ou le MX de réception"
          >
            MOCKED — Config à finaliser
          </span>
        ) : null}
      </div>

      {error && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 mb-4 flex items-start gap-2 text-xs" data-testid="bo-inbox-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" /> {error}
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-4">
        {/* Liste */}
        <div className="rounded-xl border-2 border-border bg-card overflow-hidden max-h-[600px] overflow-y-auto">
          {loading ? (
            <p className="text-sm text-foreground/50 py-10 text-center">Chargement…</p>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
              <p className="text-sm text-foreground/60">
                Aucun email reçu. La collection <code className="text-[11px] bg-muted px-1 py-0.5 rounded">inbox_emails</code> est vide.
              </p>
              <p className="text-[11px] text-foreground/40 mt-2">
                Configurer Resend inbound + MX du domaine pour recevoir les vrais emails.
              </p>
            </div>
          ) : (
            <ul>
              {items.map((m) => (
                <li
                  key={m.id}
                  onClick={() => { setSelected(m); if (!m.read) handleToggleRead(m, true); }}
                  className={`px-4 py-3 border-b border-border/60 cursor-pointer hover:bg-muted/50 transition ${selected?.id === m.id ? 'bg-muted/70' : ''}`}
                  data-testid={`inbox-item-${m.id}`}
                >
                  <div className="flex items-center gap-2">
                    {m.read ? (
                      <MailOpen className="w-3.5 h-3.5 text-foreground/40" />
                    ) : (
                      <Mail className="w-3.5 h-3.5" style={{ color: BO_BLUE }} />
                    )}
                    <div className={`text-xs ${m.read ? 'text-foreground/70' : 'text-foreground font-black'}`}>
                      {m.from_name || m.from_email}
                    </div>
                    <div className="ml-auto text-[10px] text-foreground/40">
                      {m.received_at ? new Date(m.received_at).toLocaleDateString('fr-FR') : ''}
                    </div>
                  </div>
                  <div className={`mt-1 text-[13px] ${m.read ? 'text-foreground/60' : 'text-foreground font-bold'} truncate`}>
                    {m.subject || '(sans sujet)'}
                  </div>
                  <div className="mt-0.5 text-[11px] text-foreground/50 truncate">
                    {(m.text || '').slice(0, 100)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Détail */}
        <div className="rounded-xl border-2 border-border bg-card p-5 min-h-[400px]">
          {!selected ? (
            <div className="text-center py-16 text-foreground/40">
              <Mail className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">Sélectionne un email à gauche pour le lire.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-2 pb-4 border-b border-border/50">
                <div className="min-w-0">
                  <h2 className="text-lg font-black text-foreground leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {selected.subject || '(sans sujet)'}
                  </h2>
                  <div className="text-xs text-foreground/60 mt-1">
                    De <strong>{selected.from_name || selected.from_email}</strong>
                    {selected.from_name && <> &lt;{selected.from_email}&gt;</>}
                  </div>
                  <div className="text-[11px] text-foreground/40 mt-0.5">
                    À {(selected.to || []).join(', ')} · {selected.received_at ? new Date(selected.received_at).toLocaleString('fr-FR') : ''}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleArchive(selected)} className="p-1.5 rounded hover:bg-muted" title="Archiver" data-testid="btn-inbox-archive">
                    <Archive className="w-4 h-4 text-foreground/60" />
                  </button>
                  <button onClick={() => handleDelete(selected)} className="p-1.5 rounded hover:bg-red-500/10" title="Supprimer" data-testid="btn-inbox-delete">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              <div className="py-4 text-sm text-foreground/80 whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                {selected.text || selected.html || '(pas de contenu)'}
              </div>

              {selected.replies && selected.replies.length > 0 && (
                <div className="border-t border-border/50 pt-3 mb-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 mb-2">
                    Réponses ({selected.replies.length})
                  </div>
                  {selected.replies.map((r) => (
                    <div key={r.id} className="text-xs bg-muted/50 rounded p-2 mb-1">
                      <div className="text-[10px] text-foreground/50 flex items-center gap-1 flex-wrap">
                        {r.delivered ? (
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <AlertCircle className="w-3 h-3 text-amber-500" />
                        )}
                        <span>{r.sent_by} · {r.sent_at ? new Date(r.sent_at).toLocaleString('fr-FR') : ''}</span>
                        {r.delivered && r.delivery?.id && (
                          <span className="text-emerald-600 font-mono">· Resend {r.delivery.id.slice(0, 12)}…</span>
                        )}
                        {!r.delivered && (
                          <span className="text-amber-600">· {r.delivery?.mocked ? 'MOCKED' : (r.delivery?.error || 'non délivré')}</span>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply form */}
              <div className="border-t border-border/50 pt-3">
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50 mb-2">
                  Répondre
                </div>
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Votre réponse…"
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-reply-body"
                />
                <button
                  onClick={handleSendReply}
                  disabled={sending || !replyBody.trim()}
                  className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-black hover:opacity-90 disabled:opacity-40"
                  style={{ background: BO_BLUE }}
                  data-testid="btn-send-reply"
                >
                  <Send className="w-4 h-4" /> {sending ? 'Envoi…' : 'Envoyer via Resend'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
